/**
 * Solana signing utility for Phantom and OneKey wallets
 * Provides message signing functionality similar to EIP-712 for Ethereum
 * 
 * Uses Phantom's Solana provider API (window.solana) or OneKey's Solana provider (window.$onekey.solana)
 * to sign messages with Ed25519 signatures for Solana blockchain
 */

/**
 * Solana message payload structure
 * Similar to EIP-712 message structure but for Solana
 */
export interface SolanaMessagePayload {
  action: string;
  owner: string;
  agent: string;
  expiresInDays: number;
  permissions: string[];
  nonce: string;
  agentName: string;
  timestamp?: number;
  deadline?: string;
}

/**
 * Phantom Solana Provider interface
 * Based on Phantom wallet's Solana provider API
 */
export interface PhantomSolanaProvider {
  isPhantom?: boolean;
  isConnected: boolean;
  publicKey: {
    toString(): string;
  } | null;

  connect(opts?: { onlyIfTrusted?: boolean }): Promise<{ publicKey: { toString(): string } }>;
  disconnect(): Promise<void>;
  signMessage(
    message: Uint8Array | string,
    display?: "utf8" | "hex"
  ): Promise<{
    signature: Uint8Array;
    publicKey: { toString(): string };
  }>;
  signTransaction(transaction: any): Promise<any>;
  signAllTransactions(transactions: any[]): Promise<any[]>;
  on(event: string, handler: (args: any) => void): void;
  removeListener(event: string, handler: (args: any) => void): void;
}

/**
 * OneKey Solana Provider interface
 * Based on OneKey wallet's Solana provider API (follows standard Solana wallet adapter pattern)
 */
export interface OneKeySolanaProvider {
  isConnected: boolean;
  publicKey: {
    toString(): string;
  } | null;

  connect(opts?: { onlyIfTrusted?: boolean }): Promise<{ publicKey: { toString(): string } }>;
  disconnect(): Promise<void>;
  signMessage(
    message: Uint8Array | string,
    display?: "utf8" | "hex"
  ): Promise<{
    signature: Uint8Array;
    publicKey: { toString(): string };
  }>;
  signTransaction(transaction: any): Promise<any>;
  signAllTransactions(transactions: any[]): Promise<any[]>;
  on(event: string, handler: (args: any) => void): void;
  removeListener(event: string, handler: (args: any) => void): void;
}

/**
 * Binance Solana Provider interface
 * Based on Binance wallet's Solana provider API (follows standard Solana wallet adapter pattern)
 */
export interface BinanceSolanaProvider {
  isConnected: boolean;
  publicKey: {
    toString(): string;
  } | null;

  connect(opts?: { onlyIfTrusted?: boolean }): Promise<{ publicKey: { toString(): string } }>;
  disconnect(): Promise<void>;
  signMessage(
    message: Uint8Array | string,
    display?: "utf8" | "hex"
  ): Promise<{
    signature: Uint8Array;
    publicKey: { toString(): string };
  }>;
  signTransaction(transaction: any): Promise<any>;
  signAllTransactions(transactions: any[]): Promise<any[]>;
  on?(event: string, handler: (args: any) => void): void;
  removeListener?(event: string, handler: (args: any) => void): void;
}

/**
 * Bitget Solana Provider interface
 * Based on Bitget wallet's Solana provider API (follows standard Solana wallet adapter pattern)
 * Bitget uses window.bitkeep.solana for Solana support
 */
export interface BitgetSolanaProvider {
  isBitKeep?: boolean; // Legacy BitKeep flag
  isConnected: boolean;
  publicKey: {
    toString(): string;
  } | null;

  connect(opts?: { onlyIfTrusted?: boolean }): Promise<{ publicKey: { toString(): string } }>;
  disconnect(): Promise<void>;
  signMessage(
    message: Uint8Array | string,
    display?: "utf8" | "hex"
  ): Promise<{
    signature: Uint8Array;
    publicKey: { toString(): string };
  }>;
  signTransaction(transaction: any): Promise<any>;
  signAllTransactions(transactions: any[]): Promise<any[]>;
  on?(event: string, handler: (args: any) => void): void;
  removeListener?(event: string, handler: (args: any) => void): void;
}

/**
 * TokenPocket Solana Provider interface
 * Based on TokenPocket wallet's Solana provider API (follows standard Solana wallet adapter pattern)
 * TokenPocket uses window.solana for Solana support (similar to Phantom)
 */
export interface TokenPocketSolanaProvider {
  isTokenPocket?: boolean; // TokenPocket flag
  isConnected: boolean;
  publicKey: {
    toString(): string;
  } | null;

  connect(opts?: { onlyIfTrusted?: boolean }): Promise<{ publicKey: { toString(): string } }>;
  disconnect(): Promise<void>;
  signMessage(
    message: Uint8Array | string,
    display?: "utf8" | "hex"
  ): Promise<{
    signature: Uint8Array;
    publicKey: { toString(): string };
  }>;
  signTransaction(transaction: any): Promise<any>;
  signAllTransactions(transactions: any[]): Promise<any[]>;
  on?(event: string, handler: (args: any) => void): void;
  removeListener?(event: string, handler: (args: any) => void): void;
}

/**
 * Coinbase Wallet Solana Provider interface
 * Based on Coinbase Wallet's Solana provider API (follows standard Solana wallet adapter pattern)
 * 
 * Coinbase Wallet injects window.coinbaseSolana directly, which implements the Solana provider interface.
 * The provider has methods like connect(), disconnect(), signMessage(), signTransaction(), etc.
 * Storage is accessible via _storage.scope === 'coinbaseSolana' for verification.
 * 
 * Alternative detection: window.solana.isCoinbaseWallet === true (if available)
 */
export interface CoinbaseWalletSolanaProvider {
  isCoinbaseWallet?: boolean; // Coinbase Wallet flag (may be on window.solana)
  isConnected: boolean;
  publicKey: {
    toString(): string;
  } | null;
  _storage?: {
    scope?: string; // Should be 'coinbaseSolana' for genuine Coinbase Wallet
  };
  storage?: {
    scope?: string; // Alternative storage property (without underscore)
  };

  connect(opts?: { onlyIfTrusted?: boolean }): Promise<{ publicKey: { toString(): string } }>;
  disconnect(): Promise<void>;
  signMessage(
    message: Uint8Array | string,
    display?: "utf8" | "hex"
  ): Promise<{
    signature: Uint8Array;
    publicKey: { toString(): string };
  }>;
  signTransaction(transaction: any): Promise<any>;
  signAllTransactions(transactions: any[]): Promise<any[]>;
  sendTransaction?(transaction: any): Promise<string>; // Coinbase Wallet specific method
  _request?(method: string, params?: any[]): Promise<any>; // Internal RPC request method
  on?(event: string, handler: (args: any) => void): void;
  removeListener?(event: string, handler: (args: any) => void): void;
}

/**
 * Check if Phantom Solana provider is available
 */
export function isPhantomSolanaAvailable(): boolean {
  if (typeof window === "undefined") return false;
  const provider = (window as any).solana;
  return !!(provider && provider.isPhantom);
}

/**
 * Get Phantom Solana provider
 * @returns PhantomSolanaProvider or null if not available
 */
export function getPhantomSolanaProvider(): PhantomSolanaProvider | null {
  if (typeof window === "undefined") return null;
  const provider = (window as any).solana;
  if (provider && provider.isPhantom) {
    return provider as PhantomSolanaProvider;
  }
  return null;
}

/**
 * Connect to Phantom Solana wallet
 * @param provider - Phantom Solana provider (optional, will auto-detect if not provided)
 * @returns Connected public key (Solana address in Base58)
 */
export async function connectPhantomSolana(
  provider?: PhantomSolanaProvider
): Promise<string> {
  const solanaProvider = provider || getPhantomSolanaProvider();
  
  if (!solanaProvider) {
    throw new Error("Phantom Solana wallet not found");
  }

  // Connect if not already connected
  if (!solanaProvider.isConnected) {
    const response = await solanaProvider.connect();
    return response.publicKey.toString();
  }

  // Return current public key if already connected
  if (solanaProvider.publicKey) {
    return solanaProvider.publicKey.toString();
  }

  throw new Error("Phantom Solana wallet is not connected");
}

/**
 * Sign a message with Phantom Solana wallet
 * Similar to EIP-712 signing but uses Ed25519 for Solana
 * 
 * @param provider - Phantom Solana provider
 * @param payload - Message payload to sign
 * @returns Object with signature (hex string) and public key (Base58 address)
 */
export async function signSolanaMessage(
  provider: PhantomSolanaProvider,
  payload: SolanaMessagePayload
): Promise<{ signature: string; publicKey: string }> {
  // Ensure connected
  if (!provider.isConnected) {
    await provider.connect();
  }

  // Convert payload to JSON string
  const messageString = JSON.stringify(payload);

  // Convert to bytes (UTF-8 encoding)
  const messageBytes = new TextEncoder().encode(messageString);

  // Sign message with Phantom
  const signedMessage = await provider.signMessage(messageBytes, "utf8");

  // Convert signature to hex string (64 bytes = 128 hex characters)
  // Ed25519 signatures are 64 bytes
  const signatureHex = Array.from(signedMessage.signature)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

  // Get public key as Base58 string (Solana address)
  const publicKey = signedMessage.publicKey.toString();

  return {
    signature: signatureHex,
    publicKey: publicKey,
  };
}

/**
 * Convert Solana signature (Uint8Array) to hex string
 * Helper function for signature format conversion
 */
export function solanaSignatureToHex(signature: Uint8Array): string {
  return Array.from(signature)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Check if OneKey Solana provider is available
 */
export function isOneKeySolanaAvailable(): boolean {
  if (typeof window === "undefined") return false;
  return !!(window as any).$onekey?.solana;
}

/**
 * Get OneKey Solana provider
 * @returns OneKeySolanaProvider or null if not available
 */
export function getOneKeySolanaProvider(): OneKeySolanaProvider | null {
  if (typeof window === "undefined") return null;
  const provider = (window as any).$onekey?.solana;
  if (provider) {
    return provider as OneKeySolanaProvider;
  }
  return null;
}

/**
 * Connect to OneKey Solana wallet
 * @param provider - OneKey Solana provider (optional, will auto-detect if not provided)
 * @returns Connected public key (Solana address in Base58)
 */
export async function connectOneKeySolana(
  provider?: OneKeySolanaProvider
): Promise<string> {
  const solanaProvider = provider || getOneKeySolanaProvider();
  
  if (!solanaProvider) {
    throw new Error("OneKey Solana wallet not found");
  }

  // Connect if not already connected
  if (!solanaProvider.isConnected) {
    const response = await solanaProvider.connect();
    return response.publicKey.toString();
  }

  // Return current public key if already connected
  if (solanaProvider.publicKey) {
    return solanaProvider.publicKey.toString();
  }

  throw new Error("OneKey Solana wallet is not connected");
}

/**
 * Sign a message with OneKey Solana wallet
 * Similar to EIP-712 signing but uses Ed25519 for Solana
 * 
 * @param provider - OneKey Solana provider
 * @param payload - Message payload to sign
 * @returns Object with signature (hex string) and public key (Base58 address)
 */
export async function signOneKeySolanaMessage(
  provider: OneKeySolanaProvider,
  payload: SolanaMessagePayload
): Promise<{ signature: string; publicKey: string }> {
  // Ensure connected
  if (!provider.isConnected) {
    await provider.connect();
  }

  // Convert payload to JSON string
  const messageString = JSON.stringify(payload);

  // Convert to bytes (UTF-8 encoding)
  const messageBytes = new TextEncoder().encode(messageString);

  // Sign message with OneKey
  const signedMessage = await provider.signMessage(messageBytes, "utf8");

  // Convert signature to hex string (64 bytes = 128 hex characters)
  // Ed25519 signatures are 64 bytes
  const signatureHex = Array.from(signedMessage.signature)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

  // Get public key as Base58 string (Solana address)
  const publicKey = signedMessage.publicKey.toString();

  return {
    signature: signatureHex,
    publicKey: publicKey,
  };
}

/**
 * Verify that a string is a valid Solana address (Base58)
 * Basic validation - checks length and Base58 characters
 */
export function isValidSolanaAddress(address: string): boolean {
  // Solana addresses are Base58 encoded, typically 32-44 characters
  if (!address || address.length < 32 || address.length > 44) {
    return false;
  }

  // Base58 characters: 1-9, A-H, J-N, P-Z, a-k, m-z (no 0, O, I, l)
  const base58Regex = /^[1-9A-HJ-NP-Za-km-z]+$/;
  return base58Regex.test(address);
}

/**
 * Check if Binance Solana provider is available
 * 
 * Modern Binance Web3 Wallet injects:
 * - window.solana (with Binance-specific properties)
 * - window.binancew3w.solana
 * - window.ethereum.solana
 * 
 * Legacy Binance Chain Wallet injects:
 * - window.BinanceChain.solana
 */
export function isBinanceSolanaAvailable(): boolean {
  if (typeof window === "undefined") return false;
  
  // IMPORTANT: Exclude OKX Solana first - OKX uses window.okxwallet.solana
  // If OKX Solana is detected, don't return true for window.solana as Binance
  const hasOKXSolana = !!(window as any).okxwallet?.solana;
  
  // Method 1: Modern - window.binancew3w.solana (most specific)
  if ((window as any).binancew3w?.isExtension === true && (window as any).binancew3w?.solana) {
    return true;
  }
  
  // Method 2: Modern - window.solana with Binance detection
  // Only check window.solana if OKX Solana is NOT present (to avoid false positives)
  const solana = (window as any).solana;
  if (solana && !hasOKXSolana) {
    // Check for Binance-specific flags
    if (solana.isBinance === true) {
      return true;
    }
    // Check provider name for "Binance"
    const providerName = solana.name || solana.providerName;
    if (typeof providerName === "string" && providerName.toLowerCase().includes("binance")) {
      return true;
    }
    // If binancew3w.isExtension exists AND OKX is not present, window.solana is likely Binance
    if ((window as any).binancew3w?.isExtension === true) {
      return true;
    }
  }
  
  // Method 3: window.ethereum.solana (newer format)
  if ((window as any).ethereum?.solana && !hasOKXSolana) {
    return true;
  }
  
  // Method 4: Legacy - window.BinanceChain.solana
  if ((window as any).BinanceChain?.solana) {
    return true;
  }
  
  return false;
}

/**
 * Get Binance Solana provider
 * @returns BinanceSolanaProvider or null if not available
 * 
 * Priority order:
 * 1. window.binancew3w.solana (modern)
 * 2. window.solana (if binancew3w.isExtension exists or has Binance properties)
 * 3. window.ethereum.solana (newer format)
 * 4. window.BinanceChain.solana (legacy)
 */
export function getBinanceSolanaProvider(): BinanceSolanaProvider | null {
  if (typeof window === "undefined") return null;
  
  // IMPORTANT: Exclude OKX Solana first - OKX uses window.okxwallet.solana
  // If OKX Solana is detected, don't return window.solana as Binance
  const hasOKXSolana = !!(window as any).okxwallet?.solana;
  
  // Method 1: Modern - window.binancew3w.solana (most specific)
  if ((window as any).binancew3w?.isExtension === true && (window as any).binancew3w?.solana) {
    return (window as any).binancew3w.solana as BinanceSolanaProvider;
  }
  
  // Method 2: Modern - window.solana with Binance detection
  // Only check window.solana if OKX Solana is NOT present (to avoid false positives)
  const solana = (window as any).solana;
  if (solana && !hasOKXSolana) {
    // Check for Binance-specific flags
    if (solana.isBinance === true) {
      return solana as BinanceSolanaProvider;
    }
    // Check provider name for "Binance"
    const providerName = solana.name || solana.providerName;
    if (typeof providerName === "string" && providerName.toLowerCase().includes("binance")) {
      return solana as BinanceSolanaProvider;
    }
    // If binancew3w.isExtension exists AND OKX is not present, window.solana is likely Binance
    if ((window as any).binancew3w?.isExtension === true) {
      return solana as BinanceSolanaProvider;
    }
  }
  
  // Method 3: window.ethereum.solana (newer format)
  if ((window as any).ethereum?.solana && !hasOKXSolana) {
    return (window as any).ethereum.solana as BinanceSolanaProvider;
  }
  
  // Method 4: Legacy - window.BinanceChain.solana
  if ((window as any).BinanceChain?.solana) {
    return (window as any).BinanceChain.solana as BinanceSolanaProvider;
  }
  
  return null;
}

/**
 * Connect to Binance Solana wallet
 * @param provider - Binance Solana provider (optional, will auto-detect if not provided)
 * @returns Connected public key (Solana address in Base58)
 */
export async function connectBinanceSolana(
  provider?: BinanceSolanaProvider
): Promise<string> {
  const solanaProvider = provider || getBinanceSolanaProvider();
  
  if (!solanaProvider) {
    throw new Error("Binance Solana wallet not found");
  }

  // Connect if not already connected
  if (!solanaProvider.isConnected) {
    const response = await solanaProvider.connect();
    return response.publicKey.toString();
  }

  // Return current public key if already connected
  if (solanaProvider.publicKey) {
    return solanaProvider.publicKey.toString();
  }

  throw new Error("Binance Solana wallet is not connected");
}

/**
 * Check if Bitget Solana provider is available
 * Bitget uses window.bitkeep.solana for Solana support
 * 
 * Per analysis of bitget-inpage.txt:
 * - Provider: window.bitkeep.solana (injected in init())
 * - Detection: Check if window.bitkeep && window.bitkeep.solana exists
 * - Connection: await window.bitkeep.solana.connect()
 * - Connected State: window.bitkeep.solana.connected (boolean) and publicKey
 * - Disconnection: window.bitkeep.solana.disconnect() sets publicKey = null
 */
export function isBitgetSolanaAvailable(): boolean {
  if (typeof window === "undefined") return false;
  
  const bitkeep = (window as any).bitkeep;
  if (!bitkeep) return false;
  
  // Check for Bitget Solana provider
  const solana = bitkeep.solana;
  if (solana && typeof solana.connect === "function") {
    // Additional check: verify it's actually Bitget (not a fake)
    // Per analysis: Check for isBitKeep flag or verify it's under bitkeep namespace
    if (bitkeep.isBitKeep === true || solana.isBitKeep === true) {
      return true;
    }
    // If bitkeep namespace exists with solana provider, it's likely Bitget
    // (Bitget is the only wallet that uses window.bitkeep.solana)
    return true;
  }
  
  return false;
}

/**
 * Get Bitget Solana provider
 * @returns BitgetSolanaProvider or null if not available
 */
export function getBitgetSolanaProvider(): BitgetSolanaProvider | null {
  if (typeof window === "undefined") return null;
  
  const bitkeep = (window as any).bitkeep;
  if (!bitkeep) return null;
  
  const solana = bitkeep.solana;
  if (solana && typeof solana.connect === "function") {
    return solana as BitgetSolanaProvider;
  }
  
  return null;
}

/**
 * Connect to Bitget Solana wallet
 * @param provider - Bitget Solana provider (optional, will auto-detect if not provided)
 * @returns Connected public key (Solana address in Base58)
 * 
 * Per analysis:
 * - Connection: await window.bitkeep.solana.connect()
 * - Response: { publicKey: { toString(): string } }
 * - Connected State: window.bitkeep.solana.connected (boolean) and publicKey
 * - Can also use Wallet Standard: window.bitkeep.solana.isConnected()
 */
export async function connectBitgetSolana(
  provider?: BitgetSolanaProvider
): Promise<string> {
  const solanaProvider = provider || getBitgetSolanaProvider();
  
  if (!solanaProvider) {
    throw new Error("Bitget Solana wallet not found");
  }

  // Connect if not already connected
  // Per analysis: Check connected state via isConnected or connected property
  const isConnected = solanaProvider.isConnected || 
                      (typeof (solanaProvider as any).isConnected === "function" && 
                       (solanaProvider as any).isConnected());
  
  if (!isConnected) {
    // Per analysis: await window.bitkeep.solana.connect()
    const response = await solanaProvider.connect();
    // Response contains publicKey that can be converted to string
    return response.publicKey.toString();
  }

  // Return current public key if already connected
  // Per analysis: window.bitkeep.solana.publicKey exists after connection
  if (solanaProvider.publicKey) {
    return solanaProvider.publicKey.toString();
  }

  throw new Error("Bitget Solana wallet is not connected");
}

/**
 * Sign a message with Bitget Solana wallet
 * Similar to EIP-712 signing but uses Ed25519 for Solana
 * 
 * @param provider - Bitget Solana provider
 * @param payload - Message payload to sign
 * @returns Object with signature (hex string) and public key (Base58 address)
 */
export async function signBitgetSolanaMessage(
  provider: BitgetSolanaProvider,
  payload: SolanaMessagePayload
): Promise<{ signature: string; publicKey: string }> {
  // Ensure connected
  if (!provider.isConnected) {
    await provider.connect();
  }

  // Convert payload to JSON string
  const messageString = JSON.stringify(payload);

  // Convert to bytes (UTF-8 encoding)
  const messageBytes = new TextEncoder().encode(messageString);

  // Sign message with Bitget
  const signedMessage = await provider.signMessage(messageBytes, "utf8");

  // Convert signature to hex string
  const signatureHex = Array.from(signedMessage.signature)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

  return {
    signature: signatureHex,
    publicKey: signedMessage.publicKey.toString(),
  };
}

/**
 * Check if TokenPocket Solana provider is available
 * TokenPocket uses window.solana (similar to Phantom) with window.tokenpocket root namespace indicator
 * 
 * Based on TokenPocket SDK analysis and actual provider inspection:
 * - TokenPocket injects window.tokenpocket as root namespace
 * - Uses window.solana for Solana provider (similar to Phantom)
 * - TokenPocket sets #e: 'TokenPocket' private field on window.solana provider (PRIMARY detection method)
 * - TokenPocket ALSO sets isPhantom: true for Phantom compatibility (so we check #e FIRST!)
 * - Can check tp.isConnected() if window.tp exists (SDK bridge check)
 * 
 * Detection methods (in priority order):
 * 1. PRIMARY: window.solana.name === 'TokenPocket' (simplest and most direct - confirmed via console)
 * 1b. window.solana.features.name === 'TokenPocket' (Wallet Standard property)
 * 1c. #e: 'TokenPocket' private field in window.solana (fallback via object inspection)
 * 2. window.tokenpocket root namespace exists AND window.solana exists
 * 3. tp.isConnected() returns true (SDK bridge check)
 * 4. window.ethereum.isTokenPocket === true AND window.solana exists (fallback method)
 * 5. window.solana with TokenPocket-specific properties (if available)
 * 
 * CRITICAL: TokenPocket sets isPhantom: true for compatibility, so we MUST check #e: 'TokenPocket' 
 * BEFORE excluding based on isPhantom. Otherwise, TokenPocket will be incorrectly excluded!
 */
export function isTokenPocketSolanaAvailable(): boolean {
  if (typeof window === "undefined") return false;
  
  const provider = (window as any).solana;
  if (!provider) return false;
  
  // Method 1: PRIMARY - Check window.solana.name property (simplest and most direct)
  // TokenPocket sets name: 'TokenPocket' on the solana provider (confirmed via console inspection)
  // IMPORTANT: Check this FIRST before excluding Phantom, because TokenPocket ALSO sets isPhantom: true for compatibility!
  if (provider.name === 'TokenPocket') {
    return true; // TokenPocket detected via name property (even if isPhantom: true)
  }
  
  // Method 1b: Check features.name (Wallet Standard property)
  // TokenPocket also sets features.name: 'TokenPocket' in the Wallet Standard features object
  if (provider.features && (provider.features as any).name === 'TokenPocket') {
    return true; // TokenPocket detected via features.name (even if isPhantom: true)
  }
  
  // Method 1c: Fallback - Check for TokenPocket private field #e: 'TokenPocket' via object inspection
  // TokenPocket sets a private field #e with value 'TokenPocket' on the solana provider
  // Since private fields aren't directly accessible, we check via object inspection as fallback
  try {
    const providerString = String(provider);
    const providerValues = Object.values(provider);
    
    // Check if 'TokenPocket' appears in the provider's string representation or values
    if (providerString.includes('TokenPocket') || 
        providerValues.some((v: any) => String(v) === 'TokenPocket')) {
      return true; // TokenPocket detected via #e field (even if isPhantom: true)
    }
    
    // Check for any symbol that might expose the private field
    const symbols = Object.getOwnPropertySymbols(provider);
    for (const sym of symbols) {
      const value = (provider as any)[sym];
      if (value === 'TokenPocket' || String(value) === 'TokenPocket') {
        return true; // TokenPocket detected (even if isPhantom: true)
      }
    }
  } catch (error) {
    // Ignore errors from inspection attempts
  }
  
  // Exclude Phantom - BUT only if we haven't detected TokenPocket via #e field above
  // TokenPocket sets isPhantom: true for compatibility, so we must check #e first
  if (provider.isPhantom === true) {
    return false; // It's Phantom, not TokenPocket (only if #e check didn't find TokenPocket)
  }
  
  const tokenpocket = (window as any).tokenpocket;
  const ethereum = (window as any).ethereum;
  const tp = (window as any).tp;
  
  // Method 2: Check - TokenPocket root namespace exists AND Solana provider exists
  // TokenPocket injects window.tokenpocket as a root namespace indicator
  if (typeof tokenpocket !== "undefined" && provider) {
    return true; // TokenPocket detected
  }
  
  // Method 3: Check tp.isConnected() if window.tp exists (SDK bridge check)
  // From SDK: tp.isConnected() checks for TPJSBrigeClient or webkit messageHandlers
  if (tp && typeof tp.isConnected === "function") {
    try {
      if (tp.isConnected() && provider) {
        return true; // TokenPocket detected via SDK bridge
      }
    } catch (error) {
      // isConnected() may throw in some contexts, ignore
    }
  }
  
  // Method 4: Fallback - TokenPocket Ethereum flag exists AND Solana provider exists
  // TokenPocket is a multi-chain wallet, so if ethereum.isTokenPocket is true,
  // and window.solana exists, it's likely TokenPocket's Solana provider
  if (ethereum?.isTokenPocket === true && provider) {
    return true; // TokenPocket detected
  }
  
  // Method 5: Check for TokenPocket-specific properties on solana provider (if available)
  // Some versions might set isTokenPocket directly on solana provider
  if (provider.isTokenPocket === true) {
    return true;
  }
  
  // Method 6: Legacy check - window.tp namespace (older TokenPocket versions)
  if (tp && typeof tp === "object" && provider) {
    // If tp namespace exists and solana provider exists, might be TokenPocket
    return true;
  }
  
  return false;
}

/**
 * Get TokenPocket Solana provider
 * @returns TokenPocketSolanaProvider or null if not available
 */
export function getTokenPocketSolanaProvider(): TokenPocketSolanaProvider | null {
  if (typeof window === "undefined") return null;
  
  const provider = (window as any).solana;
  if (!provider) return null;
  
  // Method 1: PRIMARY - Check window.solana.name property (simplest and most direct)
  // TokenPocket sets name: 'TokenPocket' on the solana provider (confirmed via console inspection)
  // IMPORTANT: Check this FIRST before excluding Phantom, because TokenPocket ALSO sets isPhantom: true for compatibility!
  if (provider.name === 'TokenPocket') {
    return provider as TokenPocketSolanaProvider; // TokenPocket detected via name property (even if isPhantom: true)
  }
  
  // Method 1b: Check features.name (Wallet Standard property)
  // TokenPocket also sets features.name: 'TokenPocket' in the Wallet Standard features object
  if (provider.features && (provider.features as any).name === 'TokenPocket') {
    return provider as TokenPocketSolanaProvider; // TokenPocket detected via features.name (even if isPhantom: true)
  }
  
  // Method 1c: Fallback - Check for TokenPocket private field #e: 'TokenPocket' via object inspection
  // TokenPocket sets a private field #e with value 'TokenPocket' on the solana provider
  try {
    const providerString = String(provider);
    const providerValues = Object.values(provider);
    
    if (providerString.includes('TokenPocket') || 
        providerValues.some((v: any) => String(v) === 'TokenPocket')) {
      return provider as TokenPocketSolanaProvider; // TokenPocket detected via #e field (even if isPhantom: true)
    }
    
    // Check for any symbol that might expose the private field
    const symbols = Object.getOwnPropertySymbols(provider);
    for (const sym of symbols) {
      const value = (provider as any)[sym];
      if (value === 'TokenPocket' || String(value) === 'TokenPocket') {
        return provider as TokenPocketSolanaProvider; // TokenPocket detected (even if isPhantom: true)
      }
    }
  } catch (error) {
    // Ignore errors from inspection attempts
  }
  
  // Exclude Phantom - BUT only if we haven't detected TokenPocket via #e field above
  // TokenPocket sets isPhantom: true for compatibility, so we must check #e first
  if (provider.isPhantom === true) {
    return null; // It's Phantom, not TokenPocket (only if #e check didn't find TokenPocket)
  }
  
  const tokenpocket = (window as any).tokenpocket;
  const ethereum = (window as any).ethereum;
  const tp = (window as any).tp;
  
  // Method 2: Check - TokenPocket root namespace exists
  if (typeof tokenpocket !== "undefined") {
    return provider as TokenPocketSolanaProvider;
  }
  
  // Method 3: Check tp.isConnected() if window.tp exists (SDK bridge check)
  if (tp && typeof tp.isConnected === "function") {
    try {
      if (tp.isConnected()) {
        return provider as TokenPocketSolanaProvider;
      }
    } catch (error) {
      // isConnected() may throw in some contexts, ignore
    }
  }
  
  // Method 4: Fallback - TokenPocket Ethereum flag exists
  if (ethereum?.isTokenPocket === true) {
    return provider as TokenPocketSolanaProvider;
  }
  
  // Method 5: Check for TokenPocket-specific properties
  if (provider.isTokenPocket === true) {
    return provider as TokenPocketSolanaProvider;
  }
  
  // Method 6: Legacy check - window.tp namespace
  if (tp && typeof tp === "object") {
    return provider as TokenPocketSolanaProvider;
  }
  
  return null;
}

/**
 * Connect to TokenPocket Solana wallet
 * @param provider - TokenPocket Solana provider (optional, will auto-detect if not provided)
 * @returns Connected public key (Solana address in Base58)
 */
export async function connectTokenPocketSolana(
  provider?: TokenPocketSolanaProvider
): Promise<string> {
  const solanaProvider = provider || getTokenPocketSolanaProvider();

  if (!solanaProvider) {
    throw new Error("TokenPocket Solana wallet not found");
  }

  // Connect if not already connected
  if (!solanaProvider.isConnected) {
    const response = await solanaProvider.connect();
    return response.publicKey.toString();
  }

  // Already connected, return current public key
  if (!solanaProvider.publicKey) {
    throw new Error("TokenPocket Solana wallet is connected but public key is not available");
  }

  return solanaProvider.publicKey.toString();
}

/**
 * Sign a message with TokenPocket Solana wallet
 * Similar to EIP-712 signing but uses Ed25519 for Solana
 * 
 * @param provider - TokenPocket Solana provider
 * @param payload - Message payload to sign
 * @returns Object with signature (hex string) and public key (Base58 address)
 */
export async function signTokenPocketSolanaMessage(
  provider: TokenPocketSolanaProvider,
  payload: SolanaMessagePayload
): Promise<{ signature: string; publicKey: string }> {
  // Ensure connected
  if (!provider.isConnected) {
    await provider.connect();
  }

  // Convert payload to JSON string
  const messageString = JSON.stringify(payload);

  // Convert to bytes (UTF-8 encoding)
  const messageBytes = new TextEncoder().encode(messageString);

  // Sign message with TokenPocket
  const signedMessage = await provider.signMessage(messageBytes, "utf8");

  // Convert signature to hex string
  const signatureHex = Array.from(signedMessage.signature)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

  return {
    signature: signatureHex,
    publicKey: signedMessage.publicKey.toString(),
  };
}

/**
 * Check if Coinbase Wallet Solana provider is available
 * Based on coinbase-reqsol.txt analysis:
 * - window.coinbaseSolana exists directly (created at line 13616)
 * - window.coinbaseSolana._storage.scope === 'coinbaseSolana' (storage uses underscore prefix, line 13623)
 * - Has methods: connect(), disconnect(), signMessage(), signTransaction(), sendTransaction()
 * - Has properties: isConnected, publicKey
 * 
 * NOTE: Coinbase Wallet does NOT set window.solana or window.solana.isCoinbaseWallet
 * It only creates window.coinbaseSolana directly
 */
export function isCoinbaseWalletSolanaAvailable(): boolean {
  if (typeof window === "undefined") return false;
  
  const coinbaseSolana = (window as any).coinbaseSolana;
  
  // Primary detection: Check for window.coinbaseSolana directly
  // Coinbase Wallet creates this object directly, not via window.solana
  if (coinbaseSolana && typeof coinbaseSolana === "object") {
    // Verify it's actually Coinbase Wallet by checking storage scope
    // Storage uses underscore prefix: _storage, not storage
    // From coinbase-reqsol.txt line 13623: this._storage = new e.ScopedLocalStorage("coinbaseSolana")
    const storage = coinbaseSolana._storage;
    if (storage && storage.scope === "coinbaseSolana") {
      return true;
    }
    
    // If storage check fails but coinbaseSolana exists with expected methods, still consider it valid
    // This handles cases where storage might not be immediately accessible
    // From coinbase-reqsol.txt: the object has connect, disconnect, signMessage, signTransaction methods
    if (
      typeof coinbaseSolana.connect === "function" &&
      typeof coinbaseSolana.disconnect === "function" &&
      typeof coinbaseSolana.signMessage === "function" &&
      typeof coinbaseSolana.signTransaction === "function"
    ) {
      return true;
    }
  }
  
  return false;
}

/**
 * Get Coinbase Wallet Solana provider
 * @returns CoinbaseWalletSolanaProvider or null if not available
 * 
 * Coinbase Wallet injects window.coinbaseSolana directly (not window.solana).
 * From coinbase-reqsol.txt: window.coinbaseSolana = new class extends Xi
 */
export function getCoinbaseWalletSolanaProvider(): CoinbaseWalletSolanaProvider | null {
  if (typeof window === "undefined") return null;
  
  // Coinbase Wallet creates window.coinbaseSolana directly
  const coinbaseSolana = (window as any).coinbaseSolana;
  if (coinbaseSolana && typeof coinbaseSolana === "object") {
    // Verify it's actually Coinbase Wallet by checking storage scope
    // Storage uses underscore prefix: _storage
    // From coinbase-reqsol.txt line 13623: this._storage = new e.ScopedLocalStorage("coinbaseSolana")
    const storage = coinbaseSolana._storage;
    if (storage && storage.scope === "coinbaseSolana") {
      return coinbaseSolana as CoinbaseWalletSolanaProvider;
    }
    
    // If storage check fails but coinbaseSolana exists with expected methods, still return it
    // From coinbase-reqsol.txt: the object has connect, disconnect, signMessage, signTransaction methods
    if (
      typeof coinbaseSolana.connect === "function" &&
      typeof coinbaseSolana.disconnect === "function" &&
      typeof coinbaseSolana.signMessage === "function" &&
      typeof coinbaseSolana.signTransaction === "function"
    ) {
      return coinbaseSolana as CoinbaseWalletSolanaProvider;
    }
  }
  
  return null;
}

/**
 * Connect to Coinbase Wallet Solana wallet
 * @param provider - Coinbase Wallet Solana provider (optional, will auto-detect if not provided)
 * @returns Connected public key (Solana address in Base58)
 */
export async function connectCoinbaseWalletSolana(
  provider?: CoinbaseWalletSolanaProvider
): Promise<string> {
  const solanaProvider = provider || getCoinbaseWalletSolanaProvider();
  
  if (!solanaProvider) {
    throw new Error("Coinbase Wallet Solana wallet not found");
  }

  // Connect if not already connected
  if (!solanaProvider.isConnected) {
    const response = await solanaProvider.connect();
    return response.publicKey.toString();
  }

  // Return current public key if already connected
  if (solanaProvider.publicKey) {
    return solanaProvider.publicKey.toString();
  }

  throw new Error("Coinbase Wallet Solana provider is connected but has no public key");
}

/**
 * Sign a message with Coinbase Wallet Solana wallet
 * Similar to EIP-712 signing but uses Ed25519 for Solana
 * 
 * @param provider - Coinbase Wallet Solana provider
 * @param payload - Message payload to sign
 * @returns Object with signature (hex string) and public key (Base58 address)
 */
export async function signCoinbaseWalletSolanaMessage(
  provider: CoinbaseWalletSolanaProvider,
  payload: SolanaMessagePayload
): Promise<{ signature: string; publicKey: string }> {
  // Ensure connected
  if (!provider.isConnected) {
    await provider.connect();
  }

  // Convert payload to JSON string
  const messageString = JSON.stringify(payload);

  // Convert to bytes (UTF-8 encoding)
  const messageBytes = new TextEncoder().encode(messageString);

  // Sign message with Coinbase Wallet
  const signedMessage = await provider.signMessage(messageBytes, "utf8");

  // Convert signature to hex string (64 bytes = 128 hex characters)
  // Ed25519 signatures are 64 bytes
  const signatureHex = Array.from(signedMessage.signature)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

  // Get public key as Base58 string (Solana address)
  const publicKey = signedMessage.publicKey.toString();

  return {
    signature: signatureHex,
    publicKey: publicKey,
  };
}

/**
 * Sign a message with Binance Solana wallet
 * Similar to EIP-712 signing but uses Ed25519 for Solana
 * 
 * @param provider - Binance Solana provider
 * @param payload - Message payload to sign
 * @returns Object with signature (hex string) and public key (Base58 address)
 */
export async function signBinanceSolanaMessage(
  provider: BinanceSolanaProvider,
  payload: SolanaMessagePayload
): Promise<{ signature: string; publicKey: string }> {
  // Ensure connected
  if (!provider.isConnected) {
    await provider.connect();
  }

  // Convert payload to JSON string
  const messageString = JSON.stringify(payload);

  // Convert to bytes (UTF-8 encoding)
  const messageBytes = new TextEncoder().encode(messageString);

  // Sign message with Binance
  const signedMessage = await provider.signMessage(messageBytes, "utf8");

  // Convert signature to hex string (64 bytes = 128 hex characters)
  // Ed25519 signatures are 64 bytes
  const signatureHex = Array.from(signedMessage.signature)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

  // Get public key as Base58 string (Solana address)
  const publicKey = signedMessage.publicKey.toString();

  return {
    signature: signatureHex,
    publicKey: publicKey,
  };
}

/**
 * Wallet disconnection callback type
 */
export type SolanaWalletDisconnectCallback = (walletName: 'Phantom' | 'OneKey' | 'Binance', reason?: string) => void;

/**
 * Set up disconnection detection for Phantom Solana provider
 * Monitors isConnected property and disconnect events
 * 
 * @param onDisconnect - Callback function called when wallet disconnects
 * @returns Cleanup function to remove event listeners and interval
 */
export function setupPhantomSolanaDisconnectDetection(
  onDisconnect: SolanaWalletDisconnectCallback
): () => void {
  const provider = getPhantomSolanaProvider();
  if (!provider) {
    return () => {}; // No provider, no cleanup needed
  }

  let wasConnected = provider.isConnected;
  let intervalId: NodeJS.Timeout | null = null;

  const checkConnection = () => {
    const isCurrentlyConnected = provider.isConnected;
    
    // Detect transition from connected to disconnected
    if (wasConnected && !isCurrentlyConnected) {
      console.warn('[Phantom] Solana wallet disconnected (isConnected changed)');
      onDisconnect('Phantom', 'Connection lost');
    }
    
    wasConnected = isCurrentlyConnected;
  };

  const handleDisconnect = () => {
    console.warn('[Phantom] Solana wallet disconnected (event)');
    onDisconnect('Phantom', 'Disconnected');
    wasConnected = false;
  };

  // Set up event listener if available
  if (provider.on) {
    provider.on('disconnect', handleDisconnect);
  }

  // Poll isConnected property every 2 seconds as fallback
  intervalId = setInterval(checkConnection, 2000);

  // Return cleanup function
  return () => {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    if (provider.removeListener) {
      provider.removeListener('disconnect', handleDisconnect);
    }
  };
}

/**
 * Set up disconnection detection for OneKey Solana provider
 * Monitors isConnected property and disconnect events
 * 
 * @param onDisconnect - Callback function called when wallet disconnects
 * @returns Cleanup function to remove event listeners and interval
 */
export function setupOneKeySolanaDisconnectDetection(
  onDisconnect: SolanaWalletDisconnectCallback
): () => void {
  const provider = getOneKeySolanaProvider();
  if (!provider) {
    return () => {}; // No provider, no cleanup needed
  }

  let wasConnected = provider.isConnected;
  let intervalId: NodeJS.Timeout | null = null;

  const checkConnection = () => {
    const isCurrentlyConnected = provider.isConnected;
    
    // Detect transition from connected to disconnected
    if (wasConnected && !isCurrentlyConnected) {
      console.warn('[OneKey] Solana wallet disconnected (isConnected changed)');
      onDisconnect('OneKey', 'Connection lost');
    }
    
    wasConnected = isCurrentlyConnected;
  };

  const handleDisconnect = () => {
    console.warn('[OneKey] Solana wallet disconnected (event)');
    onDisconnect('OneKey', 'Disconnected');
    wasConnected = false;
  };

  // Set up event listener if available
  if (provider.on) {
    provider.on('disconnect', handleDisconnect);
  }

  // Poll isConnected property every 2 seconds as fallback
  intervalId = setInterval(checkConnection, 2000);

  // Return cleanup function
  return () => {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    if (provider.removeListener) {
      provider.removeListener('disconnect', handleDisconnect);
    }
  };
}

/**
 * Set up disconnection detection for Binance Solana provider
 * Monitors isConnected property and disconnect events
 * 
 * @param onDisconnect - Callback function called when wallet disconnects
 * @returns Cleanup function to remove event listeners and interval
 */
export function setupBinanceSolanaDisconnectDetection(
  onDisconnect: SolanaWalletDisconnectCallback
): () => void {
  const provider = getBinanceSolanaProvider();
  if (!provider) {
    return () => {}; // No provider, no cleanup needed
  }

  let wasConnected = provider.isConnected;
  let intervalId: NodeJS.Timeout | null = null;

  const checkConnection = () => {
    const isCurrentlyConnected = provider.isConnected;
    
    // Detect transition from connected to disconnected
    if (wasConnected && !isCurrentlyConnected) {
      console.warn('[Binance] Solana wallet disconnected (isConnected changed)');
      onDisconnect('Binance', 'Connection lost');
    }
    
    wasConnected = isCurrentlyConnected;
  };

  const handleDisconnect = () => {
    console.warn('[Binance] Solana wallet disconnected (event)');
    onDisconnect('Binance', 'Disconnected');
    wasConnected = false;
  };

  // Set up event listener if available
  if (provider.on) {
    provider.on('disconnect', handleDisconnect);
  }

  // Poll isConnected property every 2 seconds as fallback
  intervalId = setInterval(checkConnection, 2000);

  // Return cleanup function
  return () => {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    if (provider.removeListener) {
      provider.removeListener('disconnect', handleDisconnect);
    }
  };
}
